/**
 * A keyboard lift started from a menu entry, on a surface that draws no grip.
 *
 * The owner removed the six-dot grips on 15 September 2026: the whole row or card already drags, so
 * the dots only said what the cursor says. The grip was also the button a keyboard lifted from, and
 * view ADR §12 keeps that path, so the lift now starts from the item's own menu ("Przestaw") and the
 * menu closes onto the ITEM, which holds `useKeyboardMove`'s `host` handlers. Arrows aim, Space or
 * Enter drops, Escape or Tab cancels, exactly as before.
 *
 * The close itself is `shared/lib/useFocusHandOff` — the same hand-over the client calendar's day
 * popover makes when it routes — and its header records why focus is moved inside `finalFocus`
 * rather than returned from it.
 */

import { type RefObject } from 'react';

import { useFocusHandOff } from '~/shared/lib/useFocusHandOff';

/** Everything the hook itself needs. */
export interface MenuLiftControl {
  /** Why the entry refuses — a consumed phase, nowhere to go. `null` when it can act. */
  readonly disabledReason: string | null;
  /** The item that holds the lift's keys once the menu has closed. */
  readonly host: RefObject<HTMLElement | null>;
  /** `useKeyboardMove`'s `lift`. */
  readonly start: () => void;
}

/**
 * A control plus the word the CALLER draws its entry with. Separate because a menu whose entry list
 * already carries its own wording (the session card's does, out of `model/sessionMenu`) would
 * otherwise have to invent a second copy of it just to satisfy the signature.
 */
export interface MenuLift extends MenuLiftControl {
  /** The entry's word — "Przestaw". */
  readonly label: string;
}

export function useMenuLift(lift: MenuLiftControl | null): {
  /** Bind to the entry's selection. */
  readonly select: () => void;
  /** Bind to the menu popup's `finalFocus`. */
  readonly finalFocus: () => boolean;
} {
  const handOff = useFocusHandOff();
  return {
    select: () => {
      if (lift === null || lift.disabledReason !== null) return;
      handOff.handOff(lift.host);
      lift.start();
    },
    finalFocus: handOff.finalFocus,
  };
}
